// IndexedDB service for storing and retrieving history data

const DB_NAME = "ExamGraderDB"
const DB_VERSION = 1
const HISTORY_STORE = "gradedHistory"
const GROUPS_STORE = "historyGroups"

// Initialize the database
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = (event) => {
      console.error("IndexedDB error:", event.target.error)
      reject("Error opening database")
    }

    request.onsuccess = (event) => {
      const db = event.target.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      // Create store for graded questions
      if (!db.objectStoreNames.contains(HISTORY_STORE)) {
        const historyStore = db.createObjectStore(HISTORY_STORE, { keyPath: "id", autoIncrement: true })
        historyStore.createIndex("groupId", "groupId", { unique: false })
        historyStore.createIndex("timestamp", "timestamp", { unique: false })
      }

      // Create store for groups
      if (!db.objectStoreNames.contains(GROUPS_STORE)) {
        const groupsStore = db.createObjectStore(GROUPS_STORE, { keyPath: "id", autoIncrement: true })
        groupsStore.createIndex("name", "name", { unique: false })
        groupsStore.createIndex("timestamp", "timestamp", { unique: false })
      }
    }
  })
}

// Add a new group
const addGroup = async (name) => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([GROUPS_STORE], "readwrite")
    const store = transaction.objectStore(GROUPS_STORE)

    const group = {
      name,
      timestamp: Date.now(),
    }

    const request = store.add(group)

    request.onsuccess = (event) => {
      resolve(event.target.result) // Returns the new group ID
    }

    request.onerror = (event) => {
      console.error("Error adding group:", event.target.error)
      reject("Error adding group")
    }
  })
}

// Get all groups
const getAllGroups = async () => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([GROUPS_STORE], "readonly")
    const store = transaction.objectStore(GROUPS_STORE)
    const request = store.getAll()

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      console.error("Error getting groups:", event.target.error)
      reject("Error getting groups")
    }
  })
}

// Update a group name
const updateGroupName = async (groupId, newName) => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([GROUPS_STORE], "readwrite")
    const store = transaction.objectStore(GROUPS_STORE)

    const getRequest = store.get(groupId)

    getRequest.onsuccess = (event) => {
      const group = event.target.result
      if (group) {
        group.name = newName
        const updateRequest = store.put(group)

        updateRequest.onsuccess = () => {
          resolve(true)
        }

        updateRequest.onerror = (event) => {
          console.error("Error updating group:", event.target.error)
          reject("Error updating group")
        }
      } else {
        reject("Group not found")
      }
    }

    getRequest.onerror = (event) => {
      console.error("Error getting group:", event.target.error)
      reject("Error getting group")
    }
  })
}

// Delete a group and all its history items
const deleteGroup = async (groupId) => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([GROUPS_STORE, HISTORY_STORE], "readwrite")

    // Delete the group
    const groupsStore = transaction.objectStore(GROUPS_STORE)
    const deleteGroupRequest = groupsStore.delete(groupId)

    // Delete all history items for this group
    const historyStore = transaction.objectStore(HISTORY_STORE)
    const index = historyStore.index("groupId")
    const historyRequest = index.openCursor(IDBKeyRange.only(groupId))

    historyRequest.onsuccess = (event) => {
      const cursor = event.target.result
      if (cursor) {
        cursor.delete()
        cursor.continue()
      }
    }

    transaction.oncomplete = () => {
      resolve(true)
    }

    transaction.onerror = (event) => {
      console.error("Error deleting group:", event.target.error)
      reject("Error deleting group")
    }
  })
}

// Add a graded question to history
const addToHistory = async (question, groupId) => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([HISTORY_STORE], "readwrite")
    const store = transaction.objectStore(HISTORY_STORE)

    const historyItem = {
      ...question,
      groupId,
      timestamp: Date.now(),
    }

    const request = store.add(historyItem)

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      console.error("Error adding to history:", event.target.error)
      reject("Error adding to history")
    }
  })
}

// Get history items for a specific group
const getHistoryByGroup = async (groupId) => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([HISTORY_STORE], "readonly")
    const store = transaction.objectStore(HISTORY_STORE)
    const index = store.index("groupId")
    const request = index.getAll(groupId)

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      console.error("Error getting history:", event.target.error)
      reject("Error getting history")
    }
  })
}

// Get all history items
const getAllHistory = async () => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([HISTORY_STORE], "readonly")
    const store = transaction.objectStore(HISTORY_STORE)
    const request = store.getAll()

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onerror = (event) => {
      console.error("Error getting all history:", event.target.error)
      reject("Error getting all history")
    }
  })
}

// Delete a history item
const deleteHistoryItem = async (id) => {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([HISTORY_STORE], "readwrite")
    const store = transaction.objectStore(HISTORY_STORE)
    const request = store.delete(id)

    request.onsuccess = () => {
      resolve(true)
    }

    request.onerror = (event) => {
      console.error("Error deleting history item:", event.target.error)
      reject("Error deleting history item")
    }
  })
}

export {
  initDB,
  addGroup,
  getAllGroups,
  updateGroupName,
  deleteGroup,
  addToHistory,
  getHistoryByGroup,
  getAllHistory,
  deleteHistoryItem,
}
